import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = '/api';

export default function FormPublic() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [classe, setClasse] = useState('');
  const [reponses, setReponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [hoverStar, setHoverStar] = useState({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/formulaires/public/${code}`),
      axios.get(`${API}/classes/public`),
    ]).then(([f, c]) => {
      setForm(f.data);
      setClasses(c.data);
      setLoading(false);
    }).catch(err => {
      setError(err.response?.data?.error || 'Formulaire introuvable');
      setLoading(false);
    });
  }, [code]);

  const setReponse = (questionId, value) => {
    setReponses(r => ({ ...r, [questionId]: value }));
  };

  const toggleCheckbox = (questionId, option) => {
    setReponses(r => {
      const current = Array.isArray(r[questionId]) ? r[questionId] : [];
      return {
        ...r,
        [questionId]: current.includes(option)
          ? current.filter(x => x !== option)
          : [...current, option]
      };
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!prenom.trim() || !nom.trim()) { alert('Prénom et nom requis'); return; }
    if (classes.length > 0 && !classe) { alert('Merci de sélectionner ta classe'); return; }

    // Vérifie les questions obligatoires
    for (const q of form.questions) {
      if (q.type === 'separateur') continue;
      if (q.obligatoire) {
        const rep = reponses[q.id];
        const empty = !rep || (Array.isArray(rep) && rep.length === 0) || String(rep).trim() === '';
        if (empty) { alert(`La question "${q.libelle}" est obligatoire.`); return; }
      }
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/eleves/soumettre`, {
        code_formulaire: code,
        prenom, nom, classe,
        reponses,
      });
      navigate('/merci');
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la soumission');
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7f9' }}>
      <div className="spinner-border" style={{ color: '#066ac9' }}></div>
    </div>
  );

  if (error) return (
    <div className="form-public-wrapper">
      <div className="form-public-card">
        <div className="form-public-header text-center">
          <i className="bi bi-x-circle" style={{ fontSize: '3rem', opacity: 0.8 }}></i>
          <h4 className="mt-3 mb-0">Formulaire indisponible</h4>
        </div>
        <div className="form-public-body text-center text-muted">
          <p>{error}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="form-public-wrapper">
      <div className="form-public-card fade-in">
        <div className="form-public-header">
          <h2 className="mb-1 fw-bold" style={{ fontSize: '1.4rem' }}>{form.titre}</h2>
          {form.description && <p className="mb-0 opacity-80" style={{ fontSize: '0.9rem' }}>{form.description}</p>}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-public-body">
            {/* Identité de l'élève */}
            <div className="question-block">
              <div className="question-label">Ton prénom <span className="text-danger">*</span></div>
              <input className="form-control" value={prenom} onChange={e => setPrenom(e.target.value)} placeholder="Prénom" required />
            </div>
            <div className="question-block">
              <div className="question-label">Ton nom <span className="text-danger">*</span></div>
              <input className="form-control" value={nom} onChange={e => setNom(e.target.value)} placeholder="Nom de famille" required />
            </div>
            <div className="question-block">
              <div className="question-label">Ta classe <span className="text-danger">*</span></div>
              {classes.length > 0 ? (
                <select className="form-select" value={classe} onChange={e => setClasse(e.target.value)} required>
                  <option value="">— Sélectionne ta classe —</option>
                  {classes.map(c => (
                    <option key={c.id} value={c.nom}>{c.nom}</option>
                  ))}
                </select>
              ) : (
                <input className="form-control" value={classe} onChange={e => setClasse(e.target.value)} placeholder="Ex: 3ème A" />
              )}
            </div>

            {/* Questions du formulaire */}
            {form.questions.map(q => {
              if (q.type === 'separateur') {
                return (
                  <div key={q.id} className="question-block">
                    <h5 className="fw-semibold text-primary mb-0" style={{ fontSize: '1rem' }}>{q.libelle}</h5>
                  </div>
                );
              }

              return (
                <div key={q.id} className="question-block">
                  <div className="question-label">
                    {q.libelle}
                    {!!q.obligatoire && <span className="text-danger ms-1">*</span>}
                  </div>

                  {q.type === 'texte_court' && (
                    <input className="form-control" value={reponses[q.id] || ''} onChange={e => setReponse(q.id, e.target.value)} placeholder={q.placeholder || ''} />
                  )}

                  {q.type === 'texte_long' && (
                    <textarea className="form-control" rows={4} value={reponses[q.id] || ''} onChange={e => setReponse(q.id, e.target.value)} placeholder={q.placeholder || ''} />
                  )}

                  {q.type === 'nombre' && (
                    <input type="number" className="form-control" value={reponses[q.id] || ''} onChange={e => setReponse(q.id, e.target.value)} placeholder={q.placeholder || ''} />
                  )}

                  {q.type === 'date' && (
                    <input type="date" className="form-control" value={reponses[q.id] || ''} onChange={e => setReponse(q.id, e.target.value)} />
                  )}

                  {q.type === 'choix_unique' && q.options?.map(o => (
                    <div key={o.id} className="form-check">
                      <input type="radio" className="form-check-input" name={`q-${q.id}`} id={`o-${o.id}`}
                        checked={reponses[q.id] === o.libelle} onChange={() => setReponse(q.id, o.libelle)} />
                      <label className="form-check-label" htmlFor={`o-${o.id}`}>{o.libelle}</label>
                    </div>
                  ))}

                  {q.type === 'choix_multiple' && q.options?.map(o => {
                    const checked = Array.isArray(reponses[q.id]) && reponses[q.id].includes(o.libelle);
                    return (
                      <div key={o.id} className="form-check">
                        <input type="checkbox" className="form-check-input" id={`o-${o.id}`}
                          checked={checked} onChange={() => toggleCheckbox(q.id, o.libelle)} />
                        <label className="form-check-label" htmlFor={`o-${o.id}`}>{o.libelle}</label>
                      </div>
                    );
                  })}

                  {q.type === 'liste' && (
                    <select className="form-select" value={reponses[q.id] || ''} onChange={e => setReponse(q.id, e.target.value)}>
                      <option value="">— Choisir —</option>
                      {q.options?.map(o => <option key={o.id} value={o.libelle}>{o.libelle}</option>)}
                    </select>
                  )}

                  {q.type === 'note' && (
                    <div className="star-rating">
                      {[1,2,3,4,5].map(s => (
                        <span key={s}
                          className={`star ${(hoverStar[q.id] || reponses[q.id] || 0) >= s ? 'active' : ''}`}
                          onMouseEnter={() => setHoverStar(h => ({ ...h, [q.id]: s }))}
                          onMouseLeave={() => setHoverStar(h => ({ ...h, [q.id]: 0 }))}
                          onClick={() => setReponse(q.id, s)}
                        >★</span>
                      ))}
                      {reponses[q.id] && (
                        <span className="text-muted ms-2" style={{ fontSize: '0.875rem' }}>{reponses[q.id]}/5</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            <button type="submit" className="btn btn-primary w-100 mt-2 py-2" disabled={submitting}>
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Envoi en cours...</>
                : <><i className="bi bi-send-fill me-2"></i>Envoyer mes réponses</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
